import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";

const TermsOfService = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <PageHeader 
        title="Terms of Service"
      />
      
      <main className="flex-grow pb-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl pt-12">
          <div className="bg-white p-8 rounded-lg shadow-sm space-y-8">
            <div>
              <h1 className="text-2xl font-bold mb-2">EMU Mentoring Pty Ltd Terms of Service</h1>
              <div className="text-sm text-gray-500 mb-6">
                Last updated: 15/09/2025
              </div>
              <p className="text-gray-700 mb-4">
                Welcome to EMU Mentoring Pty Ltd ("EMU Mentoring Pty Ltd," "we," "our," or "us"). We provide a mentoring and learning platform that connects mentors and mentees, allowing them to communicate and engage in learning experiences.
              </p>
              <p className="text-gray-700">
                These Terms of Service ("Terms") govern your access to and use of our website, applications, and related services (collectively, the "Services"). By accessing or using our Services, you agree to these Terms and our Privacy Policy. If you do not agree, you must not use the Services.
              </p>
            </div>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">1. Eligibility</h2>
              <p className="text-gray-700">
                You must be at least 18 years old and capable of entering into legally binding agreements to use our Services. By registering or using the Services, you represent and warrant that you meet these requirements.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">2. Account Registration</h2>
              <p className="text-gray-700 mb-4">To access most features, you must register for an account. You agree to:</p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>Provide accurate, current, and complete information during registration.</li>
                <li>Maintain and promptly update your information to ensure accuracy.</li>
                <li>Maintain the confidentiality of your account credentials and accept responsibility for all activities under your account.</li>
              </ul>
              <p className="text-gray-700">
                You must immediately notify us of any unauthorised access or use of your account. We reserve the right to suspend or terminate accounts that contain false, misleading, or incomplete information.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">3. Description of Services</h2>
              <p className="text-gray-700 mb-4">EMU Mentoring Pty Ltd provides a platform to connect mentors and mentees. Features may include:</p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>User profiles and skill matching.</li>
                <li>Messaging and real-time chat.</li>
                <li>Course enrolments and access to digital learning materials.</li>
                <li>Payment processing and session bookings.</li>
              </ul>
              <p className="text-gray-700">
                We provide the technology platform only. Mentoring services are offered directly by mentors. Any agreements or transactions are solely between mentors and mentees.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">4. Payments and Fees</h2>
              <p className="text-gray-700 mb-4">
                Payments for mentoring sessions, subscriptions, or other services are processed via Stripe Connect on behalf of mentors.
              </p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>EMU Mentoring Pty Ltd collects a platform fee, which may include applicable GST, before releasing the remainder to the mentor.</li>
                <li>You authorise EMU Mentoring Pty Ltd to facilitate payments and deductions on your behalf.</li>
                <li>You agree to comply with Stripe's terms of service.</li>
                <li>Fees are non-refundable except as described in our Refund Policy.</li>
                <li>You are responsible for any taxes, duties, or regulatory charges relating to payments you receive.</li>
                <li>Platform fees are subject to change, and we will provide reasonable notice before changes take effect.</li>
              </ul>
              <p className="text-gray-700">
                EMU Mentoring Pty Ltd is not a financial services provider and does not directly hold or process your payment card details.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">5. Refunds and Cancellations</h2>
              <p className="text-gray-700 mb-4">
                Refund eligibility and procedures are governed by our Refund Policy.
              </p>
              <p className="text-gray-700 mb-4">
                If a refund dispute is approved, the mentee will receive a refund equal to the original purchase amount minus the non-refundable Stripe transaction fee. Refund processing times vary depending on the user's financial institution and may take 7–10 business days.
              </p>
              <p className="text-gray-700">
                Refunds are at the sole discretion of EMU Mentoring Pty Ltd unless otherwise required by law, including under the Australian Consumer Law (ACL).
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">6. User Content and Conduct</h2>
              <p className="text-gray-700 mb-4">
                You may submit or upload content (such as profile information or course materials). By providing content, you grant EMU Mentoring Pty Ltd a worldwide, non-exclusive, royalty-free licence to use, display, reproduce, adapt, and distribute your content as necessary to operate and improve the Services.
              </p>
              <p className="text-gray-700 mb-4">You retain ownership of your content. You are solely responsible for your content and agree that it will not:</p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>Infringe intellectual property or privacy rights.</li>
                <li>Be defamatory, abusive, harassing, hateful, or otherwise objectionable.</li>
                <li>Violate applicable laws or regulations.</li>
                <li>Contain unauthorised advertising or promotional material.</li>
              </ul>
              <p className="text-gray-700">
                We reserve the right to remove content that violates these Terms or is harmful to the Services or other users.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">7. Professional Advice Disclaimer</h2>
              <p className="text-gray-700">
                Mentors on EMU Mentoring Pty Ltd may share educational information and personal experiences but are not providing regulated legal, financial, medical, or other professional advice. You acknowledge that you use mentorship content at your own risk and should consult qualified professionals for specific advice.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">8. Data Storage and Security</h2>
              <p className="text-gray-700 mb-4">
                Your data is stored and managed using Supabase, which provides authentication and database services.
              </p>
              <p className="text-gray-700">
                We implement safeguards to protect your personal information. While we strive to use industry-standard measures, no method of transmission or storage over the internet can be guaranteed as completely secure.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">9. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                All EMU Mentoring Pty Ltd-owned content, including the website, software, branding, designs, and logos, is the property of EMU Mentoring Pty Ltd or its licensors.
              </p>
              <p className="text-gray-700">
                You may not reproduce, modify, distribute, or use EMU Mentoring Pty Ltd content without prior written consent.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">10. Third-Party Services</h2>
              <p className="text-gray-700">
                Our Services integrate with third-party providers. These services are governed by their own terms and privacy policies. EMU Mentoring Pty Ltd is not responsible for third-party services.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">11. Termination</h2>
              <p className="text-gray-700 mb-4">
                We may suspend or terminate your account or access to the Services if you violate these Terms, misuse the platform, or engage in harmful, fraudulent, or unlawful behaviour.
              </p>
              <p className="text-gray-700">
                Upon termination, your right to use the Services ends immediately. However, Sections 4, 5, 6, 7, 9, 11, 12, 13, and 14 will continue to apply.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">12. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the fullest extent permitted by law, EMU Mentoring Pty Ltd and its officers, directors, employees, and agents are not liable for any indirect, incidental, consequential, special, or punitive damages, including loss of profits, data, or goodwill.
              </p>
              <p className="text-gray-700">
                Our total liability to you for any claim will not exceed the greater of AUD $500 or the total amount you paid to EMU Mentoring Pty Ltd in the 12 months preceding the event, except where liability cannot be excluded under the ACL.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">13. Indemnification</h2>
              <p className="text-gray-700">
                You agree to indemnify and hold harmless EMU Mentoring Pty Ltd and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal costs) arising from your use of the Services, your violation of these Terms, or your infringement of third-party rights.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">14. Governing Law and Dispute Resolution</h2>
              <p className="text-gray-700 mb-4">
                These Terms are governed by the laws of Queensland, Australia.
              </p>
              <p className="text-gray-700">
                Before commencing legal proceedings, the parties must attempt in good faith to resolve disputes by negotiation for at least 30 days. If unresolved, disputes shall be submitted exclusively to the courts of Queensland.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">15. Changes to Terms</h2>
              <p className="text-gray-700">
                EMU Mentoring Pty Ltd may update these Terms at any time. We will notify you of material changes via email or by posting a notice on the platform. Continued use of the Services after changes take effect constitutes acceptance of the updated Terms.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">16. Entire Agreement</h2>
              <p className="text-gray-700">
                These Terms, together with our Privacy Policy and Refund Policy, form the entire agreement between you and EMU Mentoring Pty Ltd regarding your use of the Services.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">17. Contact Us</h2>
              <p className="text-gray-700">
                Email: support@emumentoring.com
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TermsOfService;
